import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { Tabs } from '@cian/ui-kit/prev';

import * as React from 'react';

import { EMortgageOffersTabs } from '../../enums/EMortgageOffersTabs';
import { IPromo } from '../../types/promos';
import { AccreditedBanksContainer } from '../AccreditedBanks';
import { MortgageOffersContainer } from '../MortgageOffers';

import { MortgageSection } from './MortgageSection';
import { getInitialTab, getSectionTabs, getSectionTitle } from './helpers';
import { trackMortgageOffersTabClick, trackPromoOffersShow } from './tracking';
import { useMortgageCalculatorContext } from '../../contexts/MortgageCalculatorContext';

export const MortgageSectionContainer: React.FC = () => {
  const { banks, mortgagePromos } = useMortgageCalculatorContext();

  const hasBanks = !!banks.length;
  const hasMortgagePromos = !!mortgagePromos.length;

  const { tabs, title } = React.useMemo(
    () => ({
      tabs: getSectionTabs({ hasBanks, hasMortgagePromos }),
      title: getSectionTitle({ hasBanks, hasMortgagePromos }),
    }),
    [hasBanks, hasMortgagePromos],
  );

  const [activeTab, setActiveTab] = React.useState<string>(getInitialTab(tabs));

  const displayedOffersRef = React.useRef<IPromo[]>([]);

  const handleDisplayedOffersChange = React.useCallback<(displayedOffers: IPromo[]) => void>(displayedOffers => {
    displayedOffersRef.current = displayedOffers;
  }, []);

  const handleShow = React.useCallback<VoidFunction>(() => {
    trackPromoOffersShow(displayedOffersRef.current);
  }, []);

  const handleTabChange = React.useCallback<(tab: EMortgageOffersTabs) => void>(tab => {
    setActiveTab(tab);
    trackMortgageOffersTabClick(tab);
  }, []);

  const hasTabs = !!tabs.length;
  const isTabsNodeVisible = tabs.length > 1;

  if (!hasTabs) {
    return null;
  }

  return (
    <ActionAfterViewObserver triggerOnce callback={handleShow}>
      <MortgageSection
        tabs={isTabsNodeVisible && <Tabs activeTab={activeTab} tabs={tabs} onChange={handleTabChange} />}
        title={title}
      >
        {((): React.JSX.Element | null => {
          switch (activeTab) {
            case EMortgageOffersTabs.Mortgage:
              return <MortgageOffersContainer onDisplayedOffersChange={handleDisplayedOffersChange} />;
            case EMortgageOffersTabs.Banks:
              return <AccreditedBanksContainer />;
            default:
              return null;
          }
        })()}
      </MortgageSection>
    </ActionAfterViewObserver>
  );
};
